/**
 * Groups — your festival groups, without the chat.
 *
 * With the Chats tab gone (SURFACES.chats, 2026-09-22) this is the only list of
 * your groups outside Find's picker, and Find is group-scoped: a group is HOW
 * you find your friends, so it stays load-bearing even though the social layer
 * it used to live inside is hidden. A tile opens the group's home — its info page
 * (members, invite, lineup), not its message thread.
 *
 * Below your own groups, ALL GROUPS: every other group for the upcoming events you
 * are going to (operator, 2026-09-25), so someone who RSVP'd solo can find a group
 * later. A tile opens that group's page, whose join card joins or requests.
 *
 * Last, PAST GROUPS: your own groups whose event is over or cancelled, most recent first, as small tiles (operator,
 * 2026-09-29: the chat, photos and members are still worth getting back to). Other people's past groups are not
 * listed - there is nothing left to join.
 */

import { usePageLoading } from './motion/PetalLoader';
import NothingYoursYet from './ui/NothingYoursYet';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsRestoring, useQuery } from '@tanstack/react-query';
import { ChevronRight, Plus, Search, Users, X } from 'lucide-react';
import GoingOptionsModal from './rsvp/GoingOptionsModal';
import Modal, { ModalBody } from './ui/Modal';
import { useRsvpWrite } from '../hooks/mutations/useRsvpWrite';
import { handleRsvpWriteResult } from '../lib/mutations/rsvpResultHandlers';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../supabaseClient';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { useFindGroups } from '../hooks/queries/useFindGroups';
import { useMyRsvps } from '../hooks/queries/useMyRsvps';
import { isEventOver } from '../utils/eventCutoff';
import { resolveEventDisplayName } from '../utils/eventDisplayName';
import { formatEventDate } from '../utils/eventDateFormat';
import { groupHomePath } from '../config/productSurfaces';
import { groupPrimaryLabel, isGenericGroupName } from '../lib/proximity/data/userGroups';
import { STALE } from '../lib/query/staleTimes';
import GroupAvatarStack from './lineup/GroupAvatarStack';
import Button from './ui/Button';
import EventCardImage from './EventCardImage';
import './people/People.css';

import { usePromptAuth } from './auth/AuthPrompt';
/** Who is in each of your groups, for the faces on each row. One query for every group. */
function useGroupFaces(groupIds) {
  return useQuery({
    queryKey: ['groups', 'faces', groupIds],
    enabled: groupIds.length > 0,
    staleTime: STALE.user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('group_members')
        .select('group_id, user_id, profiles:user_id(id, display_name, profile_photo_url)')
        .in('group_id', groupIds)
        .is('left_at', null)
        .eq('removed', false);
      if (error) throw error;
      const byGroup = {};
      for (const row of data ?? []) {
        if (!row.profiles) continue;
        (byGroup[row.group_id] ??= []).push(row.profiles);
      }
      return byGroup;
    },
  });
}

/** Listed until the event is over: 6 AM after its last day (events.ends_at; same rule as Maps and Sets). */
function eventStillOn(event, now) {
  return !isEventOver(event, new Date(now));
}

/**
 * The upcoming events you are going to, and every group for each (list_event_groups, one call per event).
 * Past and cancelled events are dropped before any RPC. One event's call failing drops that event's groups
 * only, not the whole section; it only throws when every call failed.
 */
function useGoingEventGroups(eventIds) {
  return useQuery({
    queryKey: ['groups', 'going-event-groups', eventIds],
    enabled: eventIds.length > 0,
    staleTime: STALE.user,
    queryFn: async () => {
      const { data: events, error } = await supabase
        .from('events')
        .select(
          'event_id, event_name, display_name, display_name_override, event_date, event_datetime, ends_at, multi_day, status, card_image_url, hero_image_url, venues(name)',
        )
        .in('event_id', eventIds);
      if (error) throw error;
      const now = Date.now();
      const live = (events ?? []).filter(
        (event) => event.status !== 'cancelled' && eventStillOn(event, now),
      );
      const results = await Promise.allSettled(
        live.map(async (event) => {
          const { data, error: rpcError } = await supabase.rpc('list_event_groups', {
            p_event_id: event.event_id,
          });
          if (rpcError) throw rpcError;
          return (data ?? []).map((group) => ({ ...group, event }));
        }),
      );
      const failed = results.filter((r) => r.status === 'rejected');
      for (const r of failed) console.warn('[GroupsPage] list_event_groups failed:', r.reason);
      if (live.length > 0 && failed.length === live.length) throw failed[0].reason;
      return {
        groups: results.flatMap((r) => (r.status === 'fulfilled' ? r.value : [])),
        events: live,
      };
    },
  });
}

function formatDay(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function GroupsPage() {
  const navigate = useNavigate();
  const promptAuth = usePromptAuth();
  const { session, authReady } = useCurrentUserProfile();
  const userId = session?.user?.id ?? null;
  const { data: groups = [], isLoading } = useFindGroups(userId, { authReady });
  const { data: faces = {}, isLoading: facesLoading } = useGroupFaces(groups.map((g) => g.id));

  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const matches = (g) =>
    !q ||
    [groupPrimaryLabel(g), g.eventName, g.eventVenue].some((t) =>
      (t || '').toLowerCase().includes(q),
    );
  const upcoming = groups.filter((g) => !g.isPast && matches(g));
  const past = groups.filter((g) => g.isPast && matches(g)); // already most recent first (sortGroups)

  // Every other group for the upcoming events you are going to.
  const { data: myRsvps = [], isLoading: rsvpsLoading } = useMyRsvps(userId, {
    enabled: authReady,
  });
  const goingIds = useMemo(
    () =>
      myRsvps
        .filter((r) => r.status === 'going')
        .map((r) => r.event_id)
        .sort(),
    [myRsvps],
  );
  const { data: goingData, isLoading: eventGroupsLoading } = useGoingEventGroups(goingIds);
  const eventGroups = goingData?.groups ?? [];
  // The whole page at once (Sam, 2026-10-20): nothing shows - no title, no half a list, no faces filling in later - until
  // everything it needs is here. Saved data counts, so with the cache it shows at once and the loader never appears.
  const restoring = useIsRestoring();
  const pageLoad = usePageLoading(
    Boolean(session) &&
      (restoring ||
        !authReady ||
        isLoading ||
        rsvpsLoading ||
        (goingIds.length > 0 && eventGroupsLoading) ||
        (groups.length > 0 && facesLoading)),
    { latch: authReady && Boolean(session) },
  );
  const now = Date.now();
  const mine = new Set(groups.map((g) => g.id));
  const others = eventGroups
    .filter((g) => !g.is_member && !mine.has(g.id) && g.event.status !== 'cancelled')
    .filter((g) => eventStillOn(g.event, now))
    .filter(
      (g) =>
        !q ||
        [g.name, resolveEventDisplayName(g.event), g.event.venues?.name].some((t) =>
          (t || '').toLowerCase().includes(q),
        ),
    )
    .sort(
      (a, b) =>
        new Date(a.event.event_datetime ?? a.event.event_date) -
          new Date(b.event.event_datetime ?? b.event.event_date) || b.member_count - a.member_count,
    );

  // Events you are going to with no group of yours: a row with a + that starts one (Sam's b, 2026-10-26: the + like a
  // set's pick, no "Start a group" wording). The row itself opens the event.
  const groupedEvents = new Set(groups.map((g) => g.eventId));
  const noGroup = (goingData?.events ?? [])
    .filter((e) => !groupedEvents.has(e.event_id))
    .filter(
      (e) =>
        !q ||
        [resolveEventDisplayName(e), e.venues?.name].some((t) =>
          (t || '').toLowerCase().includes(q),
        ),
    )
    .sort(
      (a, b) =>
        new Date(a.event_datetime ?? a.event_date) - new Date(b.event_datetime ?? b.event_date),
    );
  const [creatingFor, setCreatingFor] = useState(null); // the event the name step is open for
  const [picking, setPicking] = useState(false); // the + by the search: pick the event first
  const goingEvents = [...(goingData?.events ?? [])].sort(
    (a, b) =>
      new Date(a.event_datetime ?? a.event_date) - new Date(b.event_datetime ?? b.event_date),
  );
  const rsvpWrite = useRsvpWrite(userId);
  const { showToast } = useToast();
  const createGroup = async (groupName) => {
    const event = creatingFor;
    setCreatingFor(null);
    try {
      const result = await rsvpWrite.mutateAsync({
        action: 'going_create',
        eventId: event.event_id,
        groupName,
      });
      const message = await handleRsvpWriteResult(result, { navigate, showToast });
      if (message) showToast(message);
    } catch (err) {
      showToast(err?.message || 'Could not create the group.');
    }
  };

  // Other people's groups as square tiles two across, like the events you go to alone (Sam, 2026-10-31: every group a
  // tile). The tile opens that group's page, whose join card joins or requests.
  const otherTile = (g) => {
    const eventName = resolveEventDisplayName(g.event);
    const title = g.name && !isGenericGroupName(g.name) ? g.name : `${eventName} group`;
    const isPrivate = (g.access_type || 'private') !== 'public';
    const src = g.event.card_image_url || g.event.hero_image_url;
    const state = g.has_pending_request
      ? 'Requested'
      : g.is_full
        ? 'Full'
        : isPrivate
          ? 'Private'
          : null;
    return (
      <button
        key={g.id}
        type="button"
        className="event-tile groups-tile"
        onClick={() => navigate(groupHomePath(g.id))}
      >
        <div className="event-tile__photo">
          {src ? (
            <EventCardImage
              src={src}
              alt=""
              width={320}
              height={320}
              className="event-tile__img"
              placeholderClassName="event-tile__img"
            />
          ) : (
            <div className="event-tile__img event-tile__img--none" aria-hidden>
              🎵
            </div>
          )}
          <div className="event-tile__over">
            <span className="event-tile__name">{title}</span>
            <span className="event-tile__meta">
              {[
                title !== `${eventName} group` ? eventName : null,
                formatEventDate({ event_datetime: g.event.event_datetime ?? g.event.event_date }),
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
            <span className="event-tile__meta">
              {[`${g.member_count} ${g.member_count === 1 ? 'member' : 'members'}`, state]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
        </div>
      </button>
    );
  };

  // Past groups as Profile's past events: small tiles four across, the group's name only.
  const pastTile = (g) => (
    <button
      key={g.id}
      type="button"
      className="pp-past__i"
      style={g.eventImageUrl ? { backgroundImage: `url(${g.eventImageUrl})` } : undefined}
      onClick={() => navigate(groupHomePath(g.id))}
    >
      <span>{groupPrimaryLabel(g)}</span>
    </button>
  );

  // Your groups as wide photo tiles; events you are going to alone as square tiles two across (soloTile).
  const groupTile = (g) => {
    const people = faces[g.id] ?? [];
    const meta = [formatDay(g.eventDate), g.eventVenue].filter(Boolean).join(' · ');
    return (
      <button
        key={g.id}
        type="button"
        className="event-tile groups-tile groups-tile--wide"
        onClick={() => navigate(groupHomePath(g.id))}
      >
        <div className="event-tile__photo">
          {g.eventImageUrl ? (
            <EventCardImage
              src={g.eventImageUrl}
              alt=""
              width={640}
              height={320}
              className="event-tile__img"
              placeholderClassName="event-tile__img"
            />
          ) : (
            <div className="event-tile__img event-tile__img--none" aria-hidden>
              🎵
            </div>
          )}
          <div className="event-tile__over">
            <span className="event-tile__name">{groupPrimaryLabel(g)}</span>
            <span className="event-tile__meta">
              {groupPrimaryLabel(g) !== g.eventName ? `${g.eventName} · ${meta}` : meta}
            </span>
            {people.length > 0 && (
              <span className="groups-index__faces">
                <GroupAvatarStack people={people} max={5} size="xxs" overlapPx={-6} />
                <span>
                  {people.length} {people.length === 1 ? 'member' : 'members'}
                </span>
              </span>
            )}
          </div>
        </div>
      </button>
    );
  };
  const soloTile = (e) => (
    <div
      key={e.event_id}
      className="event-tile groups-tile"
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/events/${e.event_id}`)}
    >
      <div className="event-tile__photo">
        {e.card_image_url || e.hero_image_url ? (
          <EventCardImage
            src={e.card_image_url || e.hero_image_url}
            alt=""
            width={320}
            height={320}
            className="event-tile__img"
            placeholderClassName="event-tile__img"
          />
        ) : (
          <div className="event-tile__img event-tile__img--none" aria-hidden>
            🎵
          </div>
        )}
        <div className="event-tile__over">
          <span className="event-tile__name">{resolveEventDisplayName(e)}</span>
          <span className="event-tile__meta">
            {[formatEventDate({ event_datetime: e.event_datetime ?? e.event_date }), e.venues?.name]
              .filter(Boolean)
              .join(' · ')}
          </span>
          <span className="event-tile__meta">Going alone</span>
        </div>
        <button
          type="button"
          className="event-tile__join"
          aria-label={`Create a group for ${resolveEventDisplayName(e)}`}
          onClick={(ev) => {
            ev.stopPropagation();
            setCreatingFor(e);
          }}
        >
          <Plus size={17} strokeWidth={2.6} aria-hidden />
        </button>
      </div>
    </div>
  );

  if (!session) {
    return (
      // The same signed-out screen as Find (operator, 2026-09-23): the tab's icon, a title, one line, Sign in.
      <div className="min-h-[50vh] px-[18px] py-12">
        <div className="py-24 text-center">
          <Users className="mx-auto mb-4 h-12 w-12 text-dark-muted" />
          <h1 className="mb-2 text-2xl font-bold text-dark-text">Groups</h1>
          <p className="mx-auto mb-6 max-w-md text-dark-muted">Log in to see your groups.</p>
          <Button size="lg" onClick={() => promptAuth()}>
            Log in
          </Button>
        </div>
      </div>
    );
  }
  if (pageLoad.show) return pageLoad.node;

  return (
    <div className="list-index">
      <h1 className="sr-only">Your groups</h1>
      {(groups.length > 0 || others.length > 0 || goingEvents.length > 0) && (
        <div className="groups-index__top">
          <div className="maps-page__search list-index__search">
            <Search size={15} aria-hidden="true" />
            <input
              type="search"
              value={query}
              placeholder="Search groups and events"
              aria-label="Search groups and events"
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button type="button" aria-label="Clear search" onClick={() => setQuery('')}>
                <X size={14} />
              </button>
            )}
          </div>
          {/* A new group for any event you are going to - another one for the same festival too (Sam's 3, 2026-10-26). */}
          {goingEvents.length > 0 && (
            <button
              type="button"
              className="maps-page__back"
              aria-label="New group"
              onClick={() => setPicking(true)}
            >
              <Plus size={20} aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      {upcoming.length === 0 &&
      noGroup.length === 0 &&
      others.length === 0 &&
      past.length === 0 &&
      !q &&
      !eventGroupsLoading ? (
        // The one empty state of the going-only pages (Sam, 2026-10-12).
        <NothingYoursYet text="Groups show up for events you're going to." />
      ) : (
        <>
          {(upcoming.length > 0 || noGroup.length > 0) && (
            <>
              <h2 className="list-index__label">Your groups</h2>
              {/* Tiles (Sam's pick, 2026-10-31): your groups as wide photo tiles, events you go to alone square beside
                  each other, each with a + to start a group. Every section is tiles, no rows. */}
              {upcoming.length > 0 && (
                <div className="groups-tiles groups-tiles--wide">{upcoming.map(groupTile)}</div>
              )}
              {noGroup.length > 0 && (
                <div className="event-tiles groups-tiles">{noGroup.map(soloTile)}</div>
              )}
            </>
          )}
          {others.length > 0 && (
            <>
              <h2 className="list-index__label">All groups</h2>
              <div className="event-tiles groups-tiles">{others.map(otherTile)}</div>
            </>
          )}
          {past.length > 0 && (
            <>
              <h2 className="list-index__label">Past groups</h2>
              <div className="pp-past groups-tiles">{past.map(pastTile)}</div>
            </>
          )}
          {q &&
            upcoming.length === 0 &&
            noGroup.length === 0 &&
            others.length === 0 &&
            past.length === 0 && <p className="list-index__note">No groups match.</p>}
        </>
      )}
      <Modal isOpen={picking} onClose={() => setPicking(false)} title="New group" size="md">
        <ModalBody className="!pt-0">
          <ul className="list-index__list">
            {goingEvents.map((e) => (
              <li key={e.event_id}>
                <button
                  type="button"
                  className="list-index__row"
                  onClick={() => {
                    setPicking(false);
                    setCreatingFor(e);
                  }}
                >
                  <span className="list-index__text">
                    <span className="list-index__name">{resolveEventDisplayName(e)}</span>
                    <span className="list-index__meta">
                      {[
                        formatEventDate({ event_datetime: e.event_datetime ?? e.event_date }),
                        e.venues?.name,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <ChevronRight size={17} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </ModalBody>
      </Modal>
      <GoingOptionsModal
        isOpen={Boolean(creatingFor)}
        onClose={() => setCreatingFor(null)}
        initialStep="name"
        eventId={creatingFor?.event_id}
        eventName={creatingFor ? resolveEventDisplayName(creatingFor) : undefined}
        onCreateGroup={createGroup}
        onJoinExistingGroup={(groupId) => {
          setCreatingFor(null);
          navigate(groupHomePath(groupId));
        }}
        onSkip={() => setCreatingFor(null)}
      />
    </div>
  );
}
